// The join poster: the site's name, its QR code and the three steps staff follow (as
// in the approved mock-up), as a one-page A4 PDF. It's drawn with plain PDF shapes and
// the fonts every PDF reader has built in (Helvetica), so nothing is embedded and the
// code prints sharp at any size. The same QR squares are drawn on screen.

import qrcode from 'qrcode-generator';

export const POSTER_STEPS = [
  'Install Z.A.S. from the App Store or Google Play.',
  'Open it and scan this code.',
  'Allow notifications, location “Always” and Live Activities.',
] as const;

/**
 * The QR code's squares, row by row, without the white border. Error correction
 * level Q still reads with a quarter of the code dirty or torn.
 */
export function qrModules(text: string): boolean[][] {
  const qr = qrcode(0, 'Q');
  qr.addData(text);
  qr.make();
  const n = qr.getModuleCount();
  return Array.from({ length: n }, (_, r) => Array.from({ length: n }, (_, c) => qr.isDark(r, c)));
}

/** The white border every QR code needs round it, in squares. */
export const QUIET_ZONE = 4;

// ---- Text in the built-in fonts ----

// Character widths (thousandths of the font size) for Helvetica and Helvetica-Bold,
// codes 32 to 126, from Adobe's published font metrics.
// prettier-ignore
const REGULAR = [
  278, 278, 355, 556, 556, 889, 667, 191, 333, 333, 389, 584, 278, 333, 278, 278, 556, 556, 556, 556, 556, 556, 556, 556,
  556, 556, 278, 278, 584, 584, 584, 556, 1015, 667, 667, 722, 722, 667, 611, 778, 722, 278, 500, 667, 556, 833, 722, 778,
  667, 778, 722, 667, 611, 722, 667, 944, 667, 667, 611, 278, 278, 278, 469, 556, 333, 556, 556, 500, 556, 556, 278, 556,
  556, 222, 222, 500, 222, 833, 556, 556, 556, 556, 333, 500, 278, 556, 500, 722, 500, 500, 500, 334, 260, 334, 584,
];
// prettier-ignore
const BOLD = [
  278, 333, 474, 556, 556, 889, 722, 238, 333, 333, 389, 584, 278, 333, 278, 278, 556, 556, 556, 556, 556, 556, 556, 556,
  556, 556, 333, 333, 584, 584, 584, 611, 975, 722, 722, 722, 722, 667, 611, 778, 722, 278, 556, 722, 611, 833, 722, 778,
  667, 778, 722, 667, 611, 722, 667, 944, 667, 667, 611, 333, 278, 333, 584, 556, 333, 556, 611, 556, 611, 556, 333, 611,
  611, 278, 278, 556, 278, 889, 611, 611, 611, 611, 389, 556, 333, 611, 556, 778, 556, 556, 500, 389, 280, 389, 584,
];
// The typographic marks we use, as the fonts' own codes, with their widths.
const EXTRA: Record<string, [code: number, regular: number, bold: number]> = {
  '‘': [0x91, 222, 278],
  '’': [0x92, 222, 278],
  '“': [0x93, 333, 500],
  '”': [0x94, 333, 500],
  '•': [0x95, 350, 350],
  '–': [0x96, 556, 556],
  '—': [0x97, 1000, 1000],
  '·': [0xb7, 278, 278],
};

type Font = 'regular' | 'bold';

/** A character as the font's code. Latin-1 letters (é, ü...) keep their code; anything else becomes "?". */
function code(ch: string): number {
  const c = ch.codePointAt(0) ?? 63;
  if (c >= 32 && c <= 126) return c;
  if (EXTRA[ch]) return EXTRA[ch][0];
  if (c >= 0xa0 && c <= 0xff) return c;
  return 63;
}

function charWidth(ch: string, font: Font): number {
  const c = ch.codePointAt(0) ?? 63;
  const table = font === 'bold' ? BOLD : REGULAR;
  if (c >= 32 && c <= 126) return table[c - 32];
  if (EXTRA[ch]) return EXTRA[ch][font === 'bold' ? 2 : 1];
  // Accented letters are as wide as their plain letter, near enough.
  const plain = ch.normalize('NFD')[0];
  if (plain !== ch && plain.charCodeAt(0) >= 32 && plain.charCodeAt(0) <= 126) return table[plain.charCodeAt(0) - 32];
  return 556;
}

export function textWidth(s: string, font: Font, size: number, spacing = 0): number {
  return ([...s].reduce((sum, ch) => sum + charWidth(ch, font), 0) * size) / 1000 + spacing * [...s].length;
}

/** Splits text into lines no wider than `width`. A word longer than a line is broken. */
export function wrapText(s: string, font: Font, size: number, width: number): string[] {
  const lines: string[] = [];
  let line = '';
  for (const word of s.trim().split(/\s+/)) {
    const next = line ? `${line} ${word}` : word;
    if (textWidth(next, font, size) <= width) {
      line = next;
      continue;
    }
    if (line) lines.push(line);
    line = word;
    while (textWidth(line, font, size) > width) {
      let cut = [...line].length - 1;
      while (cut > 1 && textWidth([...line].slice(0, cut).join(''), font, size) > width) cut--;
      lines.push([...line].slice(0, cut).join(''));
      line = [...line].slice(cut).join('');
    }
  }
  if (line) lines.push(line);
  return lines;
}

/** A PDF string: brackets and backslashes escaped, anything past plain ASCII as an octal code. */
function pdfString(s: string): string {
  let out = '(';
  for (const ch of s) {
    const c = code(ch);
    if (c === 0x28 || c === 0x29 || c === 0x5c) out += `\\${String.fromCharCode(c)}`;
    else if (c < 0x80) out += String.fromCharCode(c);
    else out += `\\${c.toString(8).padStart(3, '0')}`;
  }
  return `${out})`;
}

/** Text for the document's own title: UTF-16 in hex, so any language is kept. */
function pdfUnicode(s: string): string {
  let hex = 'FEFF';
  for (let i = 0; i < s.length; i++) hex += s.charCodeAt(i).toString(16).padStart(4, '0').toUpperCase();
  return `<${hex}>`;
}

const n = (x: number) => (Math.round(x * 100) / 100).toString();

// ---- The poster ----

const PAGE = { w: 595.28, h: 841.89 }; // A4, in points
const INK = '0.067 0.075 0.086'; // #111316, as on screen

export function posterPdf(siteName: string, joinCode: string): Uint8Array<ArrayBuffer> {
  const margin = 64;
  const width = PAGE.w - 2 * margin;
  const ops: string[] = [`${INK} rg`, `${INK} RG`];
  const text = (font: Font, size: number, x: number, y: number, s: string, spacing = 0) =>
    ops.push(`BT /${font === 'bold' ? 'F2' : 'F1'} ${n(size)} Tf ${n(spacing)} Tc 1 0 0 1 ${n(x)} ${n(y)} Tm ${pdfString(s)} Tj ET`);

  // The Z.A.S. mark, then "Safety alerts for <site>".
  let y = PAGE.h - margin - 12;
  text('bold', 15, margin, y, 'Z.A.S.', 3);
  y -= 58;
  const titleSize = 30;
  for (const line of wrapText(`Safety alerts for ${siteName}`, 'bold', titleSize, width)) {
    text('bold', titleSize, margin, y, line);
    y -= titleSize * 1.2;
  }

  // The steps, laid out first so the code can take whatever room is left (up to 12 cm).
  const stepSize = 17;
  const lead = stepSize * 1.4;
  const indent = 30;
  const steps = POSTER_STEPS.map((s) => wrapText(s, 'regular', stepSize, width - indent));
  const stepsHeight = steps.reduce((h, lines) => h + lines.length * lead, 0) + (steps.length - 1) * 10;

  const modules = qrModules(joinCode);
  const count = modules.length + 2 * QUIET_ZONE;
  const room = y - titleSize * 0.2 - margin - stepsHeight - 24;
  const size = Math.max(120, Math.min(340, room, width));
  const cell = size / count;
  const left = (PAGE.w - size) / 2;
  const top = y + titleSize * 0.2;
  // One path for every dark square, filled at once, so no hairlines show between them.
  const rects: string[] = [];
  modules.forEach((row, r) => {
    for (let c = 0; c < row.length; c++) {
      if (!row[c]) continue;
      let end = c;
      while (end + 1 < row.length && row[end + 1]) end++;
      const x = left + (c + QUIET_ZONE) * cell;
      const yTop = top - (r + QUIET_ZONE) * cell;
      rects.push(`${n(x)} ${n(yTop - cell)} ${n((end - c + 1) * cell)} ${n(cell)} re`);
      c = end;
    }
  });
  ops.push(rects.join('\n'), 'f');

  y = top - size - 24 - stepSize;
  steps.forEach((lines, i) => {
    text('bold', stepSize, margin, y, `${i + 1}`);
    for (const line of lines) {
      text('regular', stepSize, margin + indent, y, line);
      y -= lead;
    }
    y -= 10;
  });

  const content = ops.join('\n');
  const objects = [
    '<< /Type /Catalog /Pages 2 0 R >>',
    '<< /Type /Pages /Kids [3 0 R] /Count 1 >>',
    `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${PAGE.w} ${PAGE.h}] /Resources << /Font << /F1 4 0 R /F2 5 0 R >> >> /Contents 6 0 R >>`,
    '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica /Encoding /WinAnsiEncoding >>',
    '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold /Encoding /WinAnsiEncoding >>',
    `<< /Length ${content.length} >>\nstream\n${content}\nendstream`,
    `<< /Title ${pdfUnicode(`Z.A.S. poster · ${siteName}`)} /Producer (Z.A.S. console) >>`,
  ];
  // Everything above is plain ASCII, so string lengths are byte counts.
  let pdf = '%PDF-1.4\n';
  const offsets: number[] = [];
  objects.forEach((body, i) => {
    offsets.push(pdf.length);
    pdf += `${i + 1} 0 obj\n${body}\nendobj\n`;
  });
  const xref = pdf.length;
  pdf += `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`;
  for (const o of offsets) pdf += `${String(o).padStart(10, '0')} 00000 n \n`;
  pdf += `trailer\n<< /Size ${objects.length + 1} /Root 1 0 R /Info ${objects.length} 0 R >>\nstartxref\n${xref}\n%%EOF\n`;
  return new TextEncoder().encode(pdf);
}
